import { ReactNode, useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ContextChangeType } from '@/shared/types/tryIt';

export const CONTEXT_CHANGE_LABEL_KEY: Record<ContextChangeType, string> = {
  [ContextChangeType.UPDATED]: 'activityLogs.updated',
  [ContextChangeType.ADDED]: 'try_it.details.added',
  [ContextChangeType.REMOVED]: 'activityLogs.removed',
};

type DetailsRowProps = {
  icon: ReactNode;
  /** The row's first line. An {@link ExpandableRow} may put its whole panel here. */
  head: ReactNode;
  /** Everything under the first line: expanders, sub-lists, amend forms. */
  body?: ReactNode;
  isLast?: boolean;
  /** The phase groups draw their own left rail, so their rows skip the per-row connector. */
  showConnector?: boolean;
  indented?: boolean;
  bodyClassName?: string;
};

/** Timeline row. The icon box is one `leading-5` line tall, so it keeps the head's first line. */
const DetailsRow = ({
  icon,
  head,
  body,
  isLast = false,
  showConnector = true,
  indented = false,
  bodyClassName,
}: DetailsRowProps) => (
  <div className={cn('flex w-full flex-col', indented && 'pl-6')}>
    <div className="flex items-start gap-2 self-stretch">
      <div className="flex w-4 shrink-0 flex-col items-center self-stretch py-0.5 text-foreground-muted">
        {icon}
      </div>
      <div className="min-w-0 flex-1 leading-5">{head}</div>
    </div>
    {(body || (!isLast && showConnector)) && (
      <div className="flex gap-2">
        <div className="flex w-4 shrink-0 flex-col items-center">
          {!isLast && showConnector && <div className="my-1 w-px flex-1 bg-stroke" />}
        </div>
        <div className={cn('min-w-0 flex-1', !isLast && showConnector && 'pb-6', bodyClassName)}>
          {body}
        </div>
      </div>
    )}
  </div>
);

/** The only control on its row: the text beside it never toggles. */
export const ExpandToggle = ({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <Button
    variant="ghost-neutral"
    size="icon-xs"
    aria-expanded={expanded}
    aria-label={label}
    onClick={onToggle}
  >
    <ChevronDownIcon className={cn('transition-transform', expanded && 'rotate-180')} />
  </Button>
);

/** The chevron only appears when the row has detail to reveal. */
export const ExpandableRow = ({
  head,
  detail,
  label,
}: {
  head: ReactNode;
  detail?: ReactNode;
  label: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!detail) {
    return <>{head}</>;
  }

  return (
    <div className="flex flex-1 flex-col items-start gap-4">
      <div className="flex items-start justify-between gap-3 self-stretch">
        <span className="min-w-0 flex-1">{head}</span>
        <ExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((value) => !value)}
          label={label}
        />
      </div>

      {expanded && detail}
    </div>
  );
};

export type StepPhase = 'understand' | 'process';

const PHASE_LABEL_KEY: Record<StepPhase, string> = {
  understand: 'try_it.step_phases.understand',
  process: 'try_it.step_phases.process',
};

export type DetailRowSpec = {
  key: string;
  icon: ReactNode;
  head: ReactNode;
  body?: ReactNode;
  bodyClassName?: string;
  phase?: StepPhase;
  indented?: boolean;
};

export const PlainRow = ({ children }: { children: ReactNode }) => (
  <span className="block text-sm leading-5 text-foreground">{children}</span>
);

/** `block` so `truncate` applies — text-overflow is a no-op on an inline box. */
export const FragmentRow = ({ id }: { id: string }) => (
  <span className="block truncate text-sm leading-5 text-foreground">{id}</span>
);

const STEP_LINK_CLASS =
  'inline-flex items-center justify-center gap-2 text-sm leading-none font-medium text-foreground hover:text-informational hover:underline';

/** A value inside a step sentence. Not a link — {@link NewTabStepLink} is the linked form. */
export const StepLink = ({ children }: { children: ReactNode }) => (
  <span className="text-sm leading-none font-medium text-foreground">{children}</span>
);

/** Opens its page in a new tab, so the test session survives the click. */
export const NewTabStepLink = ({
  to,
  tooltip,
  children,
  className,
}: {
  to: string | null;
  tooltip: string;
  children: ReactNode;
  className?: string;
}) => {
  if (!to) {
    return (
      <span className={cn('text-sm leading-none font-medium text-foreground', className)}>
        {children}
      </span>
    );
  }

  return (
    <InfoTooltip content={tooltip}>
      <a href={to} target="_blank" rel="noreferrer" className={cn(STEP_LINK_CLASS, className)}>
        {children}
      </a>
    </InfoTooltip>
  );
};

export const TooltipStepLink = ({
  link,
  tooltip,
  children,
}: {
  link: { to: string } | null;
  tooltip: string;
  children: ReactNode;
}) => (
  <NewTabStepLink to={link?.to ?? null} tooltip={tooltip}>
    {children}
  </NewTabStepLink>
);

const Rows = ({
  rows,
  showConnector = true,
}: {
  rows: DetailRowSpec[];
  showConnector?: boolean;
}) => (
  <>
    {rows.map((row, index) => (
      <DetailsRow
        key={row.key}
        icon={row.icon}
        head={row.head}
        body={row.body}
        isLast={index === rows.length - 1}
        showConnector={showConnector}
        indented={row.indented}
        bodyClassName={row.bodyClassName}
      />
    ))}
  </>
);

const PhaseGroup = ({ phase, rows }: { phase: StepPhase; rows: DetailRowSpec[] }) => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-1 flex-col items-start gap-3 pb-4">
      <div className="flex items-center gap-2 self-stretch">
        <span className="size-2.5 shrink-0 rounded-full border-2 border-informational" />
        <span className="text-xs leading-4 font-medium text-foreground-muted">
          {t(PHASE_LABEL_KEY[phase])}
        </span>
      </div>
      <div className="flex gap-2 self-stretch">
        <div className="flex w-2.5 shrink-0 justify-center">
          <div className="w-px bg-stroke" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-4 self-stretch">
          <Rows rows={rows} showConnector={false} />
        </div>
      </div>
    </div>
  );
};

/** Consecutive rows of one phase, so a turn that revisits a phase says so. */
const groupByPhase = (rows: DetailRowSpec[]) =>
  rows.reduce<{ key: string; phase: StepPhase; rows: DetailRowSpec[] }[]>((groups, row) => {
    const phase = row.phase ?? 'process';
    const open = groups.at(-1);
    if (open?.phase === phase) {
      open.rows.push(row);
      return groups;
    }
    // Keyed by how many times this phase has opened, so a streaming turn does not remount it.
    const run = groups.filter((group) => group.phase === phase).length;
    return [...groups, { key: `${phase}-${run}`, phase, rows: [row] }];
  }, []);

/** Timeline of {@link DetailRowSpec}s. Falsy entries drop; `grouped` heads each run with its phase. */
export const DetailRows = ({
  rows,
  grouped = false,
}: {
  rows: (DetailRowSpec | false | null | undefined)[];
  grouped?: boolean;
}) => {
  const visible = rows.filter((row): row is DetailRowSpec => Boolean(row));

  if (!grouped) {
    return <Rows rows={visible} />;
  }

  return (
    <>
      {groupByPhase(visible).map((group) => (
        <PhaseGroup key={group.key} phase={group.phase} rows={group.rows} />
      ))}
    </>
  );
};
